import { inject } from "fw";
import { Store, handle } from "fw-state";
import { LogoutAction, StartAction } from "./actions";
import { LocalStorageCache } from "caching";
import { once } from "helpers/once";
import { AtsRepository } from "network/ats-repository";
import { AtsAutomationRuleExecutionInfo, Filter } from "models/monitor-automation-rules";
import { NestedAggregationItem, StatusAggregationItem } from "models/aggregations";
import { CurrentUserStore } from "./current-user";
import { OrganizationFilter } from "models/monitor-exports";
import { AccountType } from "models/ats-organization";

const DEFAULT_PAGE_SIZE = 20;
const REQUIRED_GRID_FIELDS = "status,name,totalApplicationCount,runDateUtc,metaData.organizationId,metaData.ruleId,actionSets";
const AGGREGATIONS =
    'terms:status date:(runDateUtc~DATEINTERVAL^"TIMEZONE" terms:status sum:actionSets.completedApplicationCount terms:(metaData.organizationId~20 sum:actionSets.completedApplicationCount))';
const CACHE_LABEL = "ats-monitoring-automation-rules-store";

const FILTER_FIELDS_MAPPING: Map<string, string> = new Map()
    .set("organization", "metaData.organizationId")
    .set("ruleId", "metaData.ruleId")
    .set("status", "status")
    .set("missing", "_missing_")
    .set("range", "runDateUtc");

interface MonitorAutomationRulesShape {
    currentUserId: string;
    loaded: boolean;
    errorLoading: boolean;
    list: AtsAutomationRuleExecutionInfo[];
    statusCounts: StatusAggregationItem[];
    dateBuckets: NestedAggregationItem[];
    total: number;
    filter: Filter;
    search: string;
    sort: string;
    runDateAggInterval: string;
    previousPageToken: string;
    nextPageToken: string;
    currentPreviousPageToken: string;
    currentNextPageToken: string;
    pageSize: number;
    pageNumber: number;
    collapsedItem: string;
    orgFilter: OrganizationFilter;
}

export class FetchListAction {
    constructor(
        public filter: Filter = null,
        public search: string = null,
        public orgFilter: OrganizationFilter = {},
        public sort: string = "",
        public runDateAggInterval: string = "1h"
    ) {}
}

export class AutomationRulesEnsureAction {}
export class AutomationRulesRefreshAction {}
export class NextPageAction {}
export class PreviousPageAction {}

export class CollapseItemAction {
    constructor(public id: string) {}
}

type SavedMonitorAutomationRulesState = {
    filter: Filter;
    search: string;
    sort: string;
    pageNumber: number;
    pageSize: number;
    runDateAggInterval: string;
    previousPageToken: string;
    nextPageToken: string;
    currentPreviousPageToken: string;
    currentNextPageToken: string;
    orgFilter: OrganizationFilter;
};

@inject
export class MonitorAutomationRulesStore extends Store<MonitorAutomationRulesShape> {
    private lastFilter: string = null;
    private lastOrgFilter: string = null;
    constructor(
        private cache: LocalStorageCache,
        private currentUser: CurrentUserStore,
        private network: AtsRepository
    ) {
        super();
    }

    defaultFilter() {
        return {};
    }

    defaultState() {
        return {
            currentUserId: null,

            loaded: false,
            errorLoading: false,
            list: [],
            statusCounts: [],
            dateBuckets: [],
            total: 0,
            filter: this.defaultFilter(),
            search: null,
            sort: "-runDateUtc",

            previousPageToken: null,
            nextPageToken: null,
            currentPreviousPageToken: null,
            currentNextPageToken: null,
            pageSize: DEFAULT_PAGE_SIZE,
            pageNumber: 1,
            runDateAggInterval: "1h",
            collapsedItem: "",
            orgFilter: { type: AccountType.Customer, tags: null }
        };
    }

    get cacheKey(): string {
        return `manager-user:${this.state.currentUserId}:${CACHE_LABEL}`;
    }

    saveState() {
        if (this.state.currentUserId == null) return;

        const saveState: SavedMonitorAutomationRulesState = {
            filter: this.state.filter,
            search: this.state.search,
            sort: this.state.sort,
            pageNumber: this.state.pageNumber,
            pageSize: this.state.pageSize,
            runDateAggInterval: this.state.runDateAggInterval,
            previousPageToken: this.state.previousPageToken,
            nextPageToken: this.state.nextPageToken,
            currentPreviousPageToken: this.state.currentPreviousPageToken,
            currentNextPageToken: this.state.currentNextPageToken,
            orgFilter: this.state.orgFilter
        };
        this.cache.set<SavedMonitorAutomationRulesState>(this.cacheKey, saveState);
    }

    restoreState() {
        if (this.state.currentUserId == null) return;

        const savedState = this.cache.get<SavedMonitorAutomationRulesState>(this.cacheKey);

        if (savedState) {
            this.setState((state) => ({
                ...state,
                filter: savedState.filter,
                search: savedState.search,
                sort: savedState.sort,
                pageNumber: savedState.pageNumber || 1,
                pageSize: savedState.pageSize || DEFAULT_PAGE_SIZE,
                runDateAggInterval: savedState.runDateAggInterval || "1h",
                previousPageToken: savedState.previousPageToken,
                nextPageToken: savedState.nextPageToken,
                currentPreviousPageToken: savedState.currentPreviousPageToken,
                currentNextPageToken: savedState.currentNextPageToken,
                orgFilter: savedState.orgFilter
            }));
        }
    }

    @handle(StartAction)
    private handleStartAction(action: StartAction) {
        this.setState(() => ({
            ...this.defaultState(),
            currentUserId: action.user.Id
        }));
    }

    @handle(CollapseItemAction)
    private collapseItemAction(action: CollapseItemAction) {
        this.setState((state) => ({
            ...state,
            collapsedItem: action.id
        }));

        if (action.id) {
            this.loadSingleItemData(action.id);
        }
    }

    @handle(LogoutAction)
    private handleLogoutAction() {
        this.setState(() => this.defaultState());
    }

    @handle(AutomationRulesEnsureAction)
    private async handleAtsOrganizationEnsureAction() {
        await once(this.cacheKey, async () => {
            if (this.state.loaded) {
                return;
            }

            this.restoreState();
        });
    }

    @handle(AutomationRulesRefreshAction)
    private handleRefreshAction(action: AutomationRulesRefreshAction) {
        if (!this.state.loaded) return;

        this.setState((state) => ({
            ...state,
            loaded: false
        }));

        this.loadPageList();
    }

    @handle(FetchListAction)
    private async handleFetchList(action: FetchListAction) {
        this.setState(() => ({
            ...this.state,
            currentUserId: this.state.currentUserId,
            filter: action.filter,
            search: action.search,
            sort: action.sort,
            runDateAggInterval: action.runDateAggInterval,
            loaded: false,
            errorLoading: false,
            pageNumber: 1,
            previousPageToken: null,
            nextPageToken: null,
            currentPreviousPageToken: null,
            currentNextPageToken: null,
            orgFilter: action.orgFilter
        }));

        await this.loadPageList();
    }

    @handle(NextPageAction)
    private async handleNextPageAction(action: NextPageAction) {
        const { nextPageToken, pageSize, pageNumber, total } = this.state;

        if (pageNumber * pageSize > total) return;

        this.setState((state) => ({
            ...state,
            loaded: false,
            pageNumber: pageNumber + 1
        }));

        await this.loadPageData(null, nextPageToken);
    }

    @handle(PreviousPageAction)
    private async handlePreviousPage(s: PreviousPageAction) {
        const { previousPageToken, pageNumber } = this.state;
        if (pageNumber <= 1) return;

        this.setState((state) => ({
            ...state,
            loaded: false,
            pageNumber: pageNumber - 1
        }));

        await this.loadPageData(previousPageToken, null);
    }

    private async loadPageList() {
        const { currentPreviousPageToken, currentNextPageToken } = this.state;
        await this.loadPageData(currentPreviousPageToken, currentNextPageToken);
    }

    private rerunningAggs: boolean = false;

    private async loadPageData(previousPageToken: string, nextPageToken: string) {
        const { pageSize, sort, search, filter, statusCounts } = this.state;
        let { runDateAggInterval } = this.state;
        let newStatusCounts = [];
        let newDateBuckets = [];
        const fieldPaths = `PreviousPageToken,NextPageToken,Total,Results(id,${REQUIRED_GRID_FIELDS})`;
        if (!this.rerunningAggs && !filter.range) runDateAggInterval = "1M";
        try {
            this.setState((state) => ({ ...state, loaded: false, errorLoading: false }));
            const filter = this.getWholeFilter(false);
            const orgFilter = this.getOrgFilter();

            const clientModelResponse = await this.network.atsAutomationRulesList(
                this.getWholeFilter(),
                search,
                sort,
                previousPageToken,
                nextPageToken,
                fieldPaths,
                AGGREGATIONS.replace("DATEINTERVAL", runDateAggInterval).replace("TIMEZONE", this.currentUser.state.timezone),
                false,
                this.getOrgFilter(),
                pageSize
            );

            if (!statusCounts || !statusCounts.length || (!filter && !orgFilter) || filter !== this.lastFilter || orgFilter !== this.lastOrgFilter) {
                const clientModelResponseAggs = await this.network.atsAutomationRulesList(
                    filter,
                    search,
                    sort,
                    previousPageToken,
                    nextPageToken,
                    fieldPaths,
                    AGGREGATIONS.replace("DATEINTERVAL", runDateAggInterval).replace("TIMEZONE", this.currentUser.state.timezone),
                    true,
                    orgFilter,
                    pageSize
                );
                newStatusCounts = clientModelResponseAggs.Aggregations["terms_status"]?.Items;
                newDateBuckets = clientModelResponseAggs.Aggregations["date_runDateUtc"]?.Items;
            } else {
                newStatusCounts = statusCounts;
                newDateBuckets = clientModelResponse?.Aggregations["date_runDateUtc"]?.Items;
            }

            if (runDateAggInterval == "1d" && newDateBuckets.length > 40) {
                this.state.runDateAggInterval = "1M";
                this.rerunningAggs = true;
                await this.loadPageData(previousPageToken, nextPageToken);
                this.rerunningAggs = false;
                return;
            } else if (runDateAggInterval == "1d" && newDateBuckets.length == 1) {
                this.state.runDateAggInterval = "1h";
                this.rerunningAggs = true;
                await this.loadPageData(previousPageToken, nextPageToken);
                this.rerunningAggs = false;
                return;
            } else if (runDateAggInterval == "1M" && newDateBuckets.length == 1) {
                this.state.runDateAggInterval = "1d";
                this.rerunningAggs = true;
                await this.loadPageData(previousPageToken, nextPageToken);
                this.rerunningAggs = false;
                return;
            } else if (runDateAggInterval == "1h" && newDateBuckets.length > 24) {
                this.state.runDateAggInterval = "1d";
                this.rerunningAggs = true;
                await this.loadPageData(previousPageToken, nextPageToken);
                this.rerunningAggs = false;
                return;
            }

            this.lastFilter = filter;
            this.lastOrgFilter = orgFilter;

            this.setState((state) => ({
                ...state,
                loaded: true,
                statusCounts: newStatusCounts,
                dateBuckets: newDateBuckets,
                previousPageToken: clientModelResponse.PreviousPageToken,
                nextPageToken: clientModelResponse.NextPageToken,
                currentPreviousPageToken: previousPageToken,
                currentNextPageToken: nextPageToken,
                runDateAggInterval: runDateAggInterval,
                list: clientModelResponse.Results,
                total: clientModelResponse.Total,
                orgFilter: this.state.orgFilter
            }));

            this.saveState();
        } catch (err) {
            this.setState((state) => ({ ...state, loaded: true, errorLoading: true }));
        }
    }

    private async loadSingleItemData(id: string) {
        try {
            // We are replacing search query with an id string to get a single result
            // const clientModelResponse = await this.network.atsExportById(id);
            // if (clientModelResponse) {
            //     this.setState((state) => ({
            //         ...state,
            //         loaded: true,
            //         list: this.state.list.map((task) => {
            //             return task.ExportTask.Id === clientModelResponse.Id
            //                 ? {
            //                       ...task,
            //                       ExportTask: clientModelResponse
            //                   }
            //                 : task;
            //         })
            //     }));
            // }
        } catch (err) {
            this.setState((state) => ({ ...state, loaded: true, errorLoading: true }));
        }
    }

    private getWholeFilter(withStatus = true) {
        const { filter } = this.state;

        if (!Object.keys(filter).length) return null;

        const filterArgs = [];

        for (const [key, value] of Object.entries(filter)) {
            if (value === undefined || value === null) continue;

            if (!withStatus && key === "status") continue;

            if (Array.isArray(value) && value.length) {
                (value as Array<string>).forEach((element) => {
                    filterArgs.push(`${FILTER_FIELDS_MAPPING.get(key)}:${element}`);
                });
            } else {
                filterArgs.push(`${FILTER_FIELDS_MAPPING.get(key)}:${value}`);
            }
        }

        return filterArgs.join(" AND ");
    }

    private getOrgFilter() {
        const { tags, type } = this.state.orgFilter;

        if (type === null && !tags?.length) return null;

        const filterArgs = [];
        if (type !== null) filterArgs.push(`metaData.type:${type}`);
        if (tags?.length) filterArgs.push(...tags.map((tag) => `tags:("${tag}")`));

        return filterArgs.join(" AND ");
    }
}
